// @ts-nocheck
/* global css */
/* eslint-disable react/static-property-placement */
/* eslint-disable react/destructuring-assignment */
import React from 'react';
import cx from 'classnames';
import type { MediaItemValue } from '@fulcrumapp/fulcrum-core';
import { DateUtils, VideoItemValue, AudioItemValue } from '@fulcrumapp/fulcrum-core';
import lineDistance from '@turf/line-distance';
import lineSlice from '@turf/line-slice';
import filesize from 'filesize';
import ShareLink from '../../shared/share-link';
import 'location-origin';
import { AppContext } from '../../hooks/use-app';
import THEME from '../../shared/theme';
import { cssInJS } from '../../utils/styles';
import type Track from './track';

interface Props {
  className: string,
  item: MediaItemValue,
  metadata?: {
    // eslint-disable-next-line camelcase
    file_size?: string,
    original?: string,
    medium?: string,
    small?: string,
    stored?: boolean,
    status?: string,
    metadata?: {
      format?: {
        tags: {
          // eslint-disable-next-line camelcase
          creation_time: string
        }
      }
      streams?: {
        // eslint-disable-next-line camelcase
        codec_type?: string,
        width: number,
        height: number,
        // eslint-disable-next-line camelcase
        avg_frame_rate: string,
        // eslint-disable-next-line camelcase
        bit_rate: string,
      }[]
    }
  },
  track?: Track,
  currentPoint?: {
    time: number,
    location: number[],
    accuracy: number,
    altitude: number,
    speed: number,
    course: number,
    viewport: number
  }
}

export default class TrackMetadataView extends React.Component<Props> {
  static styles: {
    block: string,
    container: string,
    downloadLeft: string,
    downloadLink: string,
    downloadLinkContainer: string,
    field: string,
    fields: string,
    header: string,
    label: string,
    value: string,
    processing: string,
    error: string,
  };

  static contextType = AppContext;

  get videoStream() {
    if (this.props.metadata == null || this.props?.metadata?.metadata?.streams == null) {
      return null;
    }

    return this.props.metadata.metadata.streams.find((stream) => stream.codec_type === 'video');
  }

  get isVideo() {
    return this.props.item instanceof VideoItemValue;
  }

  get audioStream() {
    if (this.props.metadata == null || this.props.metadata?.metadata?.streams == null) {
      return null;
    }

    return this.props.metadata.metadata.streams.find((stream) => stream.codec_type === 'audio');
  }

  get isAudio() {
    return this.props.item instanceof AudioItemValue;
  }

  get originalFriendlyName() {
    if (this.props.metadata == null) {
      return null;
    }

    const { videoStream } = this;

    if (videoStream == null) {
      return null;
    }

    const { width, height } = videoStream;
    let smallestDimension = height;

    if (height > width) {
      smallestDimension = width;
    }

    if (smallestDimension >= 2160) {
      return '4K';
    } if (smallestDimension >= 1080) {
      return '1080p';
    } if (smallestDimension >= 720) {
      return '720p';
    } if (smallestDimension >= 540) {
      return '540p';
    } if (smallestDimension >= 480) {
      return '480p';
    }

    return `${width} x ${height}`;
  }

  renderFieldName() {
    const { label } = this.props.item.mediaValue?.element || '';
    return this.renderField('Field name:', label);
  }

  renderDate() {
    const { metadata } = this.props.metadata || {};

    let value = null;

    if (metadata?.format?.tags?.creation_time) {
      const creationTime = metadata.format.tags.creation_time;

      let createdDateTime = new Date(creationTime);

      // If creating the date from creationTime fails getTime will return NaN
      if (Number.isNaN(createdDateTime.getTime())) {
        const [date, fullTime] = creationTime.split(' ');
        createdDateTime = new Date(`${date}T${fullTime}Z`);
      }
      value = createdDateTime.toLocaleString();
    }

    return this.renderField('Date:', value);
  }

  renderSize() {
    const fileSize = this.props.metadata?.file_size;

    let value = null;

    if (fileSize != null) {
      value = filesize(+fileSize, { round: 0 });
    }

    return this.renderField('Size:', value);
  }

  renderStartTime() {
    const { track } = this.props;

    let value = null;

    if (track && track.firstSegment && track.firstSegment.firstPoint.time != null) {
      value = new Date(track.firstSegment.firstPoint.time).toLocaleString();
    }

    return this.renderField('Start:', value);
  }

  renderCurrentTime() {
    const { currentPoint } = this.props;

    let value = null;

    if (currentPoint && currentPoint.time != null) {
      value = new Date(currentPoint.time).toLocaleString();
    }

    return this.renderField('Current:', value);
  }

  renderTotalTime() {
    const { track } = this.props;

    let value = null;

    if (track && track.firstSegment
        && track?.firstSegment?.firstPoint?.time != null
        && track?.lastSegment?.lastPoint?.time != null) {
      const diff = track.lastSegment.lastPoint.time - track.firstSegment.firstPoint.time;
      value = DateUtils.formatTimeSeconds(diff / 1000);
    }

    return this.renderField('Total:', value);
  }

  renderElapsedTime() {
    const { currentPoint, track } = this.props;

    let value = null;

    if (currentPoint && track?.firstSegment
        && track.firstSegment.firstPoint.time != null && currentPoint.time != null) {
      const diff = currentPoint.time - track.firstSegment.firstPoint.time;
      value = DateUtils.formatTimeSeconds(diff / 1000);
    }

    return this.renderField('Elapsed:', value);
  }

  renderLatitude() {
    const { currentPoint } = this.props;

    let value = null;

    if (currentPoint && currentPoint.location[0] != null) {
      value = currentPoint.location[0].toFixed(6);
    }

    return this.renderField('Latitude:', value);
  }

  renderLongitude() {
    const { currentPoint } = this.props;

    let value = null;

    if (currentPoint && currentPoint.location[1] != null) {
      value = currentPoint.location[1].toFixed(6);
    }

    return this.renderField('Longitude:', value);
  }

  renderAccuracy() {
    const { currentPoint } = this.props;

    let value = null;

    if (currentPoint && currentPoint.accuracy != null) {
      value = `${currentPoint.accuracy.toFixed(1)} m`;
    }

    return this.renderField('Accuracy:', value);
  }

  renderAltitude() {
    const { currentPoint } = this.props;

    let value = null;

    if (currentPoint && currentPoint.altitude != null) {
      value = `${currentPoint.altitude.toFixed(1)} m`;
    }

    return this.renderField('Altitude:', value);
  }

  renderSpeed() {
    const { currentPoint } = this.props;

    let value = null;

    if (currentPoint && currentPoint.speed != null) {
      value = `${(currentPoint.speed * (3600 / 1000)).toFixed(1)} km/h`;
    }

    return this.renderField('Speed:', value);
  }

  renderDirection() {
    const { currentPoint } = this.props;

    let value = null;

    if (currentPoint && currentPoint.course != null) {
      value = `${currentPoint.course.toFixed(0)}°`;
    }

    return this.renderField('Direction:', value);
  }

  renderViewport() {
    const { currentPoint } = this.props;

    let value = null;

    if (currentPoint && currentPoint.viewport != null) {
      value = `${currentPoint.viewport.toFixed(0)}°`;
    }

    return this.renderField('Viewport:', value);
  }

  renderTotalDistance() {
    const { track } = this.props;

    let value = null;

    if (track && track.segments.length) {
      const line = track.toGeoJSONLines();

      // Even if there are track segments, there may not
      // be any GeoJSON coordinates from the output.
      if (line.geometry.coordinates.length) {
        const units = 'kilometers';

        const dist = lineDistance(line, units);

        value = `${dist.toFixed(3)} km`;
      }
    }

    return this.renderField('Total Distance:', value);
  }

  renderElapsedDistance() {
    const { currentPoint, track } = this.props;

    let value = null;
    if (currentPoint && currentPoint.location && track?.segments.length) {
      const line = track?.toGeoJSONLines();

      const startPoint = {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [line.geometry.coordinates[0][0], line.geometry.coordinates[0][1]],
        },
      };

      const endPoint = {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [currentPoint.location[1], currentPoint.location[0]],
        },
      };

      const slice = lineSlice(startPoint, endPoint, line);

      const units = 'kilometers';

      const dist = lineDistance(slice, units);

      value = `${dist.toFixed(3)} km`;
    }

    return this.renderField('Distance:', value);
  }

  renderResolution() {
    let value = null;

    if (this.isVideo && this.videoStream) {
      const stream = this.videoStream;

      value = `${stream.width} x ${stream.height}`;
    }

    return this.renderField('Resolution:', value);
  }

  renderFramerate() {
    let value = null;

    if (this.isVideo && this.videoStream) {
      const stream = this.videoStream;

      const [top, bottom] = stream.avg_frame_rate.split('/');

      value = `${Math.round(+top / +bottom).toFixed(0)} fps`;
    }

    return this.renderField('Framerate:', value);
  }

  renderVideoBitrate() {
    let value = null;

    if (this.isAudio) {
      return null;
    }

    if (this.videoStream) {
      const stream = this.videoStream;

      const bitrate = +stream.bit_rate;

      value = `${(+bitrate / 1e6).toFixed(1)} Mbit`;
    }

    return this.renderField('Video Bitrate:', value);
  }

  renderAudioBitrate() {
    let value = null;

    if (this.audioStream) {
      const stream = this.audioStream;

      const bitrate = +stream.bit_rate;

      value = `${(+bitrate / 1e3).toFixed(1)} Kbit`;
    }

    return this.renderField('Audio Bitrate:', value);
  }

  // eslint-disable-next-line class-methods-use-this
  renderField(label: string, textValue: string | null) {
    const { styles } = TrackMetadataView;

    const value = textValue || 'Unavailable';

    return (
      <div className={css(styles.field)}>
        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
        <label className={css(styles.label)}>{label}</label>
        <div className={css(styles.value)}>{value}</div>
      </div>
    );
  }

  renderDownloadLinks() {
    const { styles } = TrackMetadataView;

    return (
      <div>
        <div className={css(styles.downloadLeft)}>
          <div className={css(styles.downloadLinkContainer)}>
            <a
              href={this.props.metadata?.original}
              target="_blank"
              rel="noopener noreferrer"
              className={css(styles.downloadLink)}
            >
              Original
              {' '}
              {this.originalFriendlyName && `(${this.originalFriendlyName})`}
            </a>
          </div>
          {this.props?.metadata?.medium
            && (
              <div className={css(styles.downloadLinkContainer)}>
                <a
                  href={this.props.metadata?.medium}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={css(styles.downloadLink)}
                >
                  Medium
                </a>
              </div>
            )}
        </div>
        <div className={css(styles.downloadLeft)}>
          {this.props.track && this.renderTrackDownloadLink('GPX')}
          {this.props.track && this.renderTrackDownloadLink('KML')}
          {this.props.track && this.renderTrackDownloadLink('GeoJSON')}
          {this.props.track && this.renderTrackDownloadLink('JSON')}
        </div>
      </div>
    );
  }

  renderTrackDownloadLink(type: string) {
    const { styles } = TrackMetadataView;

    const url = this.isVideo
      ? this.context.dataSource.source.getVideoTrackURL(
        this.props.item.mediaID, type.toLowerCase(),
      )
      : this.context.dataSource.source.getAudioTrackURL(
        this.props.item.mediaID, type.toLowerCase(),
      );

    return (
      <div className={css(styles.downloadLinkContainer)}>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className={css(styles.downloadLink)}
        >
          {type}
        </a>
      </div>
    );
  }

  renderShareLink() {
    if (!this.context.params.data_share_id) {
      return null;
    }

    const type = this.isVideo ? 'videos' : 'audio';

    const { currentPoint, track } = this.props;

    let time = '0';

    if (currentPoint && track?.firstSegment.firstPoint.time != null && currentPoint.time != null) {
      const diff = currentPoint.time - track.firstSegment.firstPoint.time;
      time = DateUtils.formatTimeSeconds(diff / 1000, true);
    }

    const url = `${globalThis.location.origin}/shares/${this.context.params.data_share_id}/${type}/${this.props.item.mediaID}/play?t=${time}`;

    return (
      <ShareLink url={url} />
    );
  }

  renderStatus() {
    const { styles } = TrackMetadataView;

    const renderStatusMessage = (style: string, message: string) => (
      <div className={css(styles.block, style)}>
        <h1 className={css(styles.header)}>Status</h1>
        <div>
          {message}
        </div>
      </div>
    );

    const { status } = this.props.metadata || { status: 'COMPLETE' };
    if (['THUMBNAILS_FAILED', 'RESIZE_FAILED'].indexOf(status) > -1) {
      return renderStatusMessage(styles.error, 'Something went wrong while processing this video. Make sure the video is playable and upload it again.');
    }
    if (status === 'PROCESSING') {
      return renderStatusMessage(styles.processing,
        'This video is still being processed. Thumbnails and previews will be available soon.');
    }
    return '';
  }

  renderMetadata() {
    const { styles } = TrackMetadataView;

    if (this.props.metadata == null) {
      return null;
    }

    return (
      <div>
        {this.renderStatus()}
        {this.context.params.data_share_id
          && (
            <div className={css(styles.block)}>
              <h1 className={css(styles.header)}>Share</h1>

              <div className={css(styles.fields)}>
                {this.renderShareLink()}
              </div>
            </div>
          )}

        {this.props.metadata && this.props.metadata.stored
          && (
            <div className={css(styles.block)}>
              <h1 className={css(styles.header)}>Download</h1>

              <div className={css(styles.fields)}>
                {this.renderDownloadLinks()}
              </div>
            </div>
          )}

        <div className={css(styles.block)}>
          <h1 className={css(styles.header)}>Metadata</h1>

          <div className={css(styles.fields)}>
            {this.renderFieldName()}
            {this.renderDate()}
            {this.renderResolution()}
            {this.renderSize()}
            {this.renderStartTime()}
            {this.renderCurrentTime()}
            {this.renderTotalTime()}
            {this.renderElapsedTime()}
            {this.renderLatitude()}
            {this.renderLongitude()}
            {this.renderAccuracy()}
            {this.renderAltitude()}
            {this.renderSpeed()}
            {this.renderDirection()}
            {this.renderViewport()}
            {this.renderTotalDistance()}
            {this.renderElapsedDistance()}
            {this.renderFramerate()}
            {this.renderVideoBitrate()}
            {this.renderAudioBitrate()}
          </div>
        </div>
      </div>
    );
  }

  render() {
    const { styles } = TrackMetadataView;

    return (
      <div className={cx(this.props.className, styles.container)}>
        {this.renderMetadata()}
      </div>
    );
  }
}

const styles = cssInJS({
  container: {
    fontFamily: THEME.fontFamily,
    backgroundColor: '#fff',
    borderLeft: '1px solid #dbdbdb',
  },

  block: {
    padding: 20,
    borderBottom: '1px solid #dbdbdb',
    fontSize: 13,
  },

  header: {
    fontWeight: 'bold',
    marginBottom: 4,
    fontSize: '100%',
  },

  fields: {
    paddingTop: 8,
  },

  field: {
    padding: '3px 0',
  },

  label: {
    display: 'inline-block',
    fontSize: 12,
    width: '40%',
    fontWeight: 'bold',
    verticalAlign: 'top',
    marginBottom: 0,
    lineHeight: '12px',
  },

  value: {
    display: 'inline-block',
    fontSize: 12,
    width: '60%',
    verticalAlign: 'top',
    lineHeight: '12px',
  },

  downloadLinkContainer: {
    marginBottom: 8,
  },

  downloadLink: {
    color: THEME.linkColor,
    textDecoration: 'none',
  },

  downloadLeft: {
    display: 'inline-table',
    width: 120,
  },

  processing: {
    color: '#0c5460',
    backgroundColor: '#d1ecf1',
  },
  error: {
    color: '#ba2a2a',
    backgroundColor: '#f8d7da',
  },
});

TrackMetadataView.styles = styles;
